import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import HabboImage from '../HabboImage';
import { useSiteTheme, resolveThemeAssetUrl } from '../../data/siteTheme';
import { offsetWithin, firstItemAnchor, logoTileDeltaY, tileSnapShift } from '../../data/bannerGrid';

const BANNER_HEIGHT = 128;
const HABBO_LIFT = 2;
const FALLBACK_TILE_WIDTH = 64;

const EXACT_PIXEL_IMAGE = {
    width: 'auto',
    height: 'auto',
    maxWidth: 'none',
    maxHeight: 'none',
    objectFit: 'none',
    flexShrink: 0
};

function StaffPanelBanner({ userData }) {
    const navigate = useNavigate();
    const habboName = userData?.habboName || userData?.username || '';

    const theme = useSiteTheme();
    const { logo, bannerTile } = theme.config;

    const bannerRef = useRef(null);
    const rowRef = useRef(null);
    const logoRef = useRef(null);
    const habboRef = useRef(null);

    const [tileWidth, setTileWidth] = useState(FALLBACK_TILE_WIDTH);
    const [layout, setLayout] = useState({ tileX: -bannerTile.alignOffset, tileY: 0, habboShift: 0 });

    useEffect(() => {
        const src = resolveThemeAssetUrl(bannerTile.url);
        if (!src) return;

        const probe = new Image();
        probe.onload = () => setTileWidth(probe.naturalWidth || FALLBACK_TILE_WIDTH);
        probe.src = src;
    }, [bannerTile.url]);

    const updateLayout = useCallback(() => {
        const banner = bannerRef.current;
        const row = rowRef.current;
        const logoImage = logoRef.current;
        if (!banner || !row || !logoImage) return;

        const config = theme.config;
        const logoHeight = logoImage.naturalHeight || logoImage.offsetHeight;
        const logoPos = logoImage.offsetParent
            ? offsetWithin(logoImage, banner)
            : firstItemAnchor(row, banner, logoHeight);

        const tileX = Math.round(logoPos.x - config.bannerTile.alignOffset);
        const tileY = Math.round(logoPos.y - logoTileDeltaY(config, logoHeight));

        const habbo = habboRef.current;
        let habboShift = 0;

        if (habbo && habbo.offsetWidth > 0) {
            const habboPos = offsetWithin(habbo, banner);
            habboShift = tileSnapShift(habboPos.x - tileX, tileWidth);
        }

        setLayout(prev =>
            prev.tileX === tileX && prev.tileY === tileY && prev.habboShift === habboShift
                ? prev
                : { tileX, tileY, habboShift }
        );
    }, [theme.config, tileWidth]);

    useEffect(() => {
        updateLayout();
        window.addEventListener('resize', updateLayout);
        return () => window.removeEventListener('resize', updateLayout);
    }, [updateLayout, logo.url, habboName]);

    return (
        <div
            ref={bannerRef}
            className="relative z-30 w-full bg-repeat"
            style={{
                backgroundImage: `url(${resolveThemeAssetUrl(bannerTile.url)})`,
                backgroundPosition: `${layout.tileX}px ${layout.tileY}px`,
                boxShadow: '0 3px 0 0 rgba(0,0,0,0.35)',
                imageRendering: 'pixelated'
            }}
        >
            <div
                ref={rowRef}
                className="relative mx-auto flex w-full max-w-[1400px] items-center gap-4 px-4"
                style={{ height: BANNER_HEIGHT }}
            >
                <img
                    ref={logoRef}
                    src={resolveThemeAssetUrl(logo.url)}
                    alt="Frankly Origins"
                    draggable={false}
                    onClick={() => navigate('/staff')}
                    onLoad={updateLayout}
                    className="hidden cursor-pointer md:block"
                    style={{ ...EXACT_PIXEL_IMAGE, imageRendering: 'pixelated' }}
                />

                <div className="flex min-w-0 flex-1 flex-col justify-center">
                    <span
                        className="font-['Jersey_10'] text-[24px] uppercase leading-none tracking-[2px] text-gold sm:text-[28px] md:text-[34px]"
                        style={{ textShadow: '2px 2px 0 rgba(0,0,0,0.6)' }}
                    >
                        Staff Panel [BETA]
                    </span>
                    <span
                        className="font-['Jersey_10'] text-[15px] uppercase leading-tight tracking-[1px] text-white/70 md:text-[17px]"
                        style={{ textShadow: '1px 1px 0 rgba(0,0,0,0.5)' }}
                    >
                        This staff panel is under development, <br />please report issues to Steve/Rhyss.
                    </span>
                </div>

                {habboName && (
                    <div
                        ref={habboRef}
                        className="flex h-full shrink-0 items-end gap-2"
                        style={{ transform: `translateX(-${layout.habboShift}px)` }}
                    >
                        <HabboImage
                            habbo={habboName}
                            hotel={userData?.hotel || 'COM'}
                            direction={2}
                            head_direction={3}
                            size="b"
                            style={{ ...EXACT_PIXEL_IMAGE, marginBottom: HABBO_LIFT }}
                            alt={habboName}
                            onLoad={updateLayout}
                        />
                        <div className="flex flex-col justify-center self-center">
                            <a
                                href="/"
                                className="shrink-0 border mb-3 border-black/30 bg-black/25 px-3 py-[6px] font-['Jersey_10'] text-[16px] uppercase tracking-[1px] text-white transition-transform hover:-translate-y-[1px] active:translate-y-[1px]"
                                style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.4)' }}
                            >
                                Back to Frankly
                            </a>

                            <span
                                className="font-['Jersey_10'] text-[20px] uppercase leading-none tracking-[1px] text-white"
                                style={{ textShadow: '1px 1px 0 rgba(0,0,0,0.5)' }}
                            >
                                {habboName}
                            </span>
                            <span
                                className="font-['Jersey_10'] text-[17px] uppercase leading-tight tracking-[1px] text-white/70"
                                style={{ textShadow: '1px 1px 0 rgba(0,0,0,0.5)' }}
                            >
                                {userData?.hasAdminRole ? 'Administrator' : 'Staff'} access
                            </span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default StaffPanelBanner;
